import { useRef, useState, useEffect } from "react";
import "./App.scss";
import { useLiveAPIContext } from "./contexts/LiveAPIContext";
import LandingPage from "./components/LandingPage/LandingPage";
import { Altair } from "./components/altair/Altair";
import ControlTray from "./components/control-tray/ControlTray";
import cn from "classnames";
import { SpecialtySelector } from './components/specialty-selector/SpecialtySelector';
import { Logo } from './components/logo/Logo';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Login } from './components/auth/Login';
import { UserProfile } from './components/user-profile/UserProfile';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { Settings } from './components/settings/Settings';
import { checkSubscriptionStatus } from './utils/subscriptionChecker';
import { Header } from './components/Header';
import { ProductTour } from './components/product-tour/ProductTour';
import { RegistrationPrompt } from './components/registration-prompt/RegistrationPrompt';
import { Chat } from './components/chat/Chat';
import Notes from './components/notes/Notes';
import {
  isFirstTimeUser,
  isOnboardingCompleted,
  markOnboardingComplete,
  isFirstCaseCompleted,
  markFirstCaseComplete,
  markFirstTimeUserComplete
} from './utils/firstTimeUser';
import { History } from './components/history/History';
import { SessionSummaryPopup } from './components/session-summary-popup/SessionSummaryPopup';
import { Register } from './components/auth/Register';
import { IntermediatePage } from './components/auth/IntermediatePage';
import { PrivateRoute } from './components/PrivateRoutes';
import { ForgotPassword } from './components/auth/ForgotPassword';
import { CaseReview } from './components/case-review/CaseReview';
import { AdminDashboard } from './components/admin/AdminDashboard';
import { SessionSummary } from './components/session-summary/SessionSummary';
import BlogPage from './pages/BlogPage';
import BlogPostPage from './pages/BlogPostPage';
import BlogManager from './components/admin/BlogManager';

const API_KEY = process.env.REACT_APP_GEMINI_API_KEY;
const validatedApiKey: string = API_KEY || "";

function App() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [videoStream, setVideoStream] = useState<MediaStream | null>(null);
  const [selectedContext, setSelectedContext] = useState<{
    specialty: string;
    topic: string;
    description: string;
    gender: 'male' | 'female';
  } | null>(null);
  const { setScenarioContext } = useLiveAPIContext();
  const [showProductTour, setShowProductTour] = useState(false);
  const [showRegistrationPrompt, setShowRegistrationPrompt] = useState(false);
  const [showSessionSummary, setShowSessionSummary] = useState(false);
  const firstTimeUser = isFirstTimeUser();
  const navigate = useNavigate();

  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 2000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (firstTimeUser && !isOnboardingCompleted() && !loading) {
      setShowProductTour(true);
    }
  }, [firstTimeUser, loading]);

  useEffect(() => {
    if (isFirstCaseCompleted() && !user) {
      setShowRegistrationPrompt(true);
    }
  }, [user]);

  useEffect(() => {
    if (selectedContext) {
      console.log('Selected context:', selectedContext);
    }
  }, [selectedContext]);

  useEffect(() => {
    const testApiKey = async () => {
      try {
        console.log('Testing Gemini API connection...');

        const testUrl = 'https://generativelanguage.googleapis.com/v1beta/models/gemini-pro/generateContent';
        console.log('Endpoint:', testUrl);

        const response = await fetch(`${testUrl}?key=${validatedApiKey}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            contents: [{
              parts: [{ text: "Hello" }]
            }]
          })
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => null);
          throw new Error(
            `API test failed: ${response.status} ${response.statusText}\n` +
            `Error details: ${JSON.stringify(errorData, null, 2)}`
          );
        }

        const data = await response.json();
        console.log('API test successful:', data);

      } catch (error) {
        console.error('API test details:', {
          error,
          apiKey: validatedApiKey ? 'Present' : 'Missing',
          apiKeyLength: validatedApiKey?.length
        });

        // Check for specific error types
        if (error instanceof TypeError && error.message === 'Failed to fetch') {
          console.error('Network error - Check your internet connection or if the API endpoint is blocked');
        }
      }
    };

    testApiKey();
  }, []);

  useEffect(() => {
    // Check subscription status every hour
    checkSubscriptionStatus();
    const interval = setInterval(checkSubscriptionStatus, 3600000);
    return () => clearInterval(interval);
  }, []);


  const [automatedSummary, setAutomatedSummary] = useState<{
    ipsScore: number;
    dataGatheringScore: number;
    diagnosisMgtScore: number;
    areasForImprovement: string;
  } | null>(null);

  const handleTopicSelect = (specialty: any, topic: any) => {
    const context = {
      specialty: specialty.name,
      topic: topic.name,
      description: topic.description,
      gender: topic.gender || 'male',
    };
    setSelectedContext(context);
    setScenarioContext(context);
    setAutomatedSummary(null); // Clear previous summary
  };

  const handleProductTourComplete = () => {
    setShowProductTour(false);
    markOnboardingComplete();
  };

  const handleCaseComplete = (summary?: {
    ipsScore: number;
    dataGatheringScore: number;
    diagnosisMgtScore: number;
    areasForImprovement: string;
  }) => {
    if (summary) {
      setAutomatedSummary(summary);
    }

    if (!user) {
      // For non-logged in users, always redirect to intermediate page after first case
      markFirstCaseComplete();
      navigate('/intermediate');
      return;
    }

    // For logged in users, show session summary
    setShowSessionSummary(true);
  };


  const handleSessionSummaryClose = () => {
    setShowSessionSummary(false);
    // Reset selected context to prevent the popup from showing again
    setSelectedContext(null);
  };

  const handleRegistrationPromptClose = () => {
    setShowRegistrationPrompt(false);
    markFirstTimeUserComplete();
  };

  // Sync premium_users data periodically (without reloading)
  useEffect(() => {
    const syncSubscriptions = async () => {
      try {
        const response = await fetch('/premium_users.json');
        const remoteData = await response.json();
        const localData = JSON.parse(localStorage.getItem('premium_users') || '{}');

        if (JSON.stringify(remoteData) !== JSON.stringify(localData)) {
          localStorage.setItem('premium_users', JSON.stringify(remoteData));
          // Silently updated — no reload needed
        }
      } catch (error) {
        console.error('Sync error:', error);
      }
    };

    syncSubscriptions();
    const interval = setInterval(syncSubscriptions, 300000);
    return () => clearInterval(interval);
  }, []);


  if (loading) {
    return (
      <div className="preloader">
        <Logo animate={true} />
        <h1>SimplePLAB<sup>2</sup> <br />v3.0</h1>
        <div className="loading-bar"></div>
      </div>
    );
  }

  // Only show login page if not first time user and not logged in
  if (!firstTimeUser && !user && !isFirstCaseCompleted()) {
    return (
      <Routes>
        <Route path="/register" element={<Register />} />
        <Route path="/intermediate" element={<IntermediatePage />} />
        <Route path="*" element={<Login />} />
      </Routes>
    );
  }

  // Redirect to intermediate page if user completed a case but isn't logged in
  if (!user && isFirstCaseCompleted()) {
    return (
      <Routes>
        <Route path="/register" element={<Register />} />
        <Route path="/intermediate" element={<IntermediatePage />} />
        <Route path="/login" element={<Login />} />
        <Route path="*" element={<Navigate to="/intermediate" replace />} />
      </Routes>
    );
  }

  return (
    <div className="app-container">
      <Header />
      <main className="content-section">
        <Routes>
          <Route path="/" element={
            <>
              <div className="main-app-area">
                <SpecialtySelector onTopicSelect={handleTopicSelect} />
                <Altair onCaseComplete={handleCaseComplete} />
                <video
                  className={cn("stream", {
                    hidden: !videoRef.current || !videoStream,
                  })}
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                />
                <ControlTray
                  videoRef={videoRef}
                  onVideoStreamChange={setVideoStream}
                  supportsVideo={true}
                />
              </div>

              {showProductTour && (
                <ProductTour
                  isOpen={showProductTour}
                  onComplete={handleProductTourComplete}
                  onExit={handleProductTourComplete}
                />
              )}
              {showRegistrationPrompt && (
                <RegistrationPrompt
                  isOpen={showRegistrationPrompt}
                  onClose={handleRegistrationPromptClose}
                />
              )}
              {showSessionSummary && selectedContext && (
                <SessionSummaryPopup
                  isOpen={showSessionSummary}
                  onClose={handleSessionSummaryClose}
                  specialtyName={selectedContext.specialty}
                  topicName={selectedContext.topic}
                  automatedSummary={automatedSummary}
                />
              )}
            </>
          } />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/intermediate" element={<IntermediatePage />} />
          <Route path="/settings" element={<PrivateRoute element={<Settings />} />} />
          <Route path="/history" element={<PrivateRoute element={<History />} />} />
          <Route path="/case-review/:id" element={<PrivateRoute element={<CaseReview />} />} />
          <Route path="/admin" element={<PrivateRoute element={<AdminDashboard />} />} />
          <Route path="/admin/blog" element={<PrivateRoute element={<BlogManager />} />} />
          <Route path="/session-summary" element={<PrivateRoute element={<SessionSummary />} />} />
          <Route path="/session-summary-embed" element={<SessionSummary embedded={true} />} />
          <Route path="/chat" element={<PrivateRoute element={<Chat />} />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />
        </Routes>
      </main>
      <Notes />
    </div >
  );
}

export default function AppWithAuth() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <App />
      </AuthProvider>
    </BrowserRouter>
  );
}
